import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  Descriptions,
  Input,
  Pagination,
  Select,
  Skeleton,
  Table,
  Tabs,
  Tag,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  EditOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import moment from "moment";
import * as XLSX from "xlsx";
import PageHeader from "../../../component/pageHeaders";
import { API } from "../../../config/API";
import { GET } from "../../../utils/apirequest";
import { countryLabel, formatMoney } from "../../../config/countries";
import { useEpaperConfig } from "../../../config/epaperConfig";
import { regionLabel } from "../../../config/regions";

type TabKey = "registered" | "subscribers";

interface Agent {
  id: number;
  username: string;
  email: string;
  phone_number?: string | null;
  code?: string | null;
  region?: string | null;
  country?: string | null;
  zone?: string | null;
  district?: string | null;
  custom_district?: string | null;
  blocked?: boolean;
  role?: { id: number; name: string } | null;
}

interface RegisteredUser {
  id: number;
  full_name: string;
  email: string;
  phone: string;
  status: string;
  subscriber_code?: string | null;
  amount?: string | null;
  register_mode?: string | null;
  subscription_end_date?: string | null;
  createdAt: string;
}

interface SubscriptionRow {
  id: number;
  amount: string;
  payment_mode: string;
  payment_date: string;
  receipt_number: string;
  subscription_start_date: string;
  subscription_end_date: string;
  status: string;
  agent_code?: string | null;
  subscription_plan?: { id: number; plan_name: string } | null;
  epaper_user?: {
    id: number;
    full_name?: string | null;
    email?: string | null;
    phone?: string | null;
    subscriber_code?: string | null;
  } | null;
  createdAt: string;
}

const paymentModeLabels: Record<string, string> = {
  cash: "Cash",
  online: "Online (PayTabs)",
  bank_transfer: "Bank Transfer",
  cheque: "Cheque",
  upi: "UPI",
};

// Mirrors the `status` enum on api::epaper-subscription.
const subscriptionStatusOptions = [
  { label: "Active", value: "active" },
  { label: "Expired", value: "expired" },
  { label: "Cancelled", value: "cancelled" },
];

// One request pulls everything matching the current filters for the export.
const EXPORT_PAGE_SIZE = 5000;

const statusColor = (status: string) => {
  switch (status) {
    case "active":
      return "green";
    case "expired":
      return "orange";
    case "cancelled":
      return "red";
    case "refunded":
      return "volcano";
    case "paused":
      return "gold";
    case "inactive":
      return "orange";
    default:
      return "default";
  }
};

const formatDate = (date?: string | null) =>
  date ? moment(date).format("DD-MM-YYYY") : "—";

function AgentView() {
  const navigate = useNavigate();
  const { id } = useParams();
  // Country names come from E-Paper Settings.
  useEpaperConfig();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get("tab") as TabKey) || "registered";

  const [agent, setAgent] = useState<Agent | null>(null);
  const [agentLoading, setAgentLoading] = useState(true);
  const [tab, setTab] = useState<TabKey>(initialTab);

  // Registered users tab state
  const [regData, setRegData] = useState<RegisteredUser[]>([]);
  const [regLoading, setRegLoading] = useState(false);
  const [regPage, setRegPage] = useState(1);
  const [regPageSize, setRegPageSize] = useState(10);
  const [regTotal, setRegTotal] = useState(0);
  const [regSearch, setRegSearch] = useState("");
  const regTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Subscribers tab state
  const [subData, setSubData] = useState<SubscriptionRow[]>([]);
  const [subLoading, setSubLoading] = useState(false);
  const [subPage, setSubPage] = useState(1);
  const [subPageSize, setSubPageSize] = useState(10);
  const [subTotal, setSubTotal] = useState(0);
  const [subSearch, setSubSearch] = useState("");
  const [subStatus, setSubStatus] = useState<string[]>([]);
  const [subExporting, setSubExporting] = useState(false);
  const subTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load the agent record once.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setAgentLoading(true);
      try {
        const res: any = await GET(
          `${API.USERS}/${id}?populate=role`,
          null,
          null
        );
        if (!cancelled && res?.id) setAgent(res);
      } catch (err) {
        console.error("Agent load failed:", err);
        message.error("Failed to load agent");
      } finally {
        if (!cancelled) setAgentLoading(false);
      }
    };
    if (id) run();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const fetchRegistered = async (
    page: number,
    pageSize: number,
    search: string
  ) => {
    if (!id) return;
    setRegLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        createdById: String(id),
        ...(search && { search }),
      });
      const res: any = await GET(
        `${API.EPAPER_USERS_LIST}?${params.toString()}`,
        null,
        null
      );
      if (res?.data) {
        setRegData(res.data);
        setRegTotal(res.pagination?.total || 0);
      } else {
        setRegData([]);
        setRegTotal(0);
      }
    } catch (err) {
      console.error("Registered users load failed:", err);
      setRegData([]);
      setRegTotal(0);
    } finally {
      setRegLoading(false);
    }
  };

  const buildSubParams = (
    page: number,
    pageSize: number,
    search: string,
    status: string[]
  ) =>
    new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
      createdById: String(id),
      ...(search && { search }),
      ...(status.length && { status: status.join(",") }),
    });

  const fetchSubscriptions = async (
    page: number,
    pageSize: number,
    search: string,
    status: string[]
  ) => {
    if (!id) return;
    setSubLoading(true);
    try {
      const params = buildSubParams(page, pageSize, search, status);
      const res: any = await GET(
        `${API.EPAPER_USERS_SUBSCRIPTIONS_LIST}?${params.toString()}`,
        null,
        null
      );
      if (res?.data) {
        setSubData(res.data);
        setSubTotal(res.pagination?.total || 0);
      } else {
        setSubData([]);
        setSubTotal(0);
      }
    } catch (err) {
      console.error("Subscriptions load failed:", err);
      setSubData([]);
      setSubTotal(0);
    } finally {
      setSubLoading(false);
    }
  };

  // Trigger fetch when the agent id is known + on pagination changes.
  useEffect(() => {
    if (!id) return;
    fetchRegistered(regPage, regPageSize, regSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, regPage, regPageSize]);

  useEffect(() => {
    if (!id) return;
    fetchSubscriptions(subPage, subPageSize, subSearch, subStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, subPage, subPageSize]);

  // Debounced search per tab — reset to first page on change.
  useEffect(() => {
    if (regTimer.current) clearTimeout(regTimer.current);
    regTimer.current = setTimeout(() => {
      setRegPage(1);
      fetchRegistered(1, regPageSize, regSearch);
    }, 400);
    return () => {
      if (regTimer.current) clearTimeout(regTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regSearch]);

  useEffect(() => {
    if (subTimer.current) clearTimeout(subTimer.current);
    subTimer.current = setTimeout(() => {
      setSubPage(1);
      fetchSubscriptions(1, subPageSize, subSearch, subStatus);
    }, 400);
    return () => {
      if (subTimer.current) clearTimeout(subTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subSearch, subStatus]);

  // Export the subscribers currently matching the search + status filters.
  const handleSubExport = async () => {
    if (!id) return;
    setSubExporting(true);
    try {
      const params = buildSubParams(1, EXPORT_PAGE_SIZE, subSearch, subStatus);
      const res: any = await GET(
        `${API.EPAPER_USERS_SUBSCRIPTIONS_LIST}?${params.toString()}`,
        null,
        null
      );
      const rows: SubscriptionRow[] = res?.data || [];
      if (!rows.length) {
        message.warning("No subscriptions to export for the selected filters");
        return;
      }

      const sheetRows = rows.map((r, i) => ({
        "S.No": i + 1,
        "Subscriber Code": r.epaper_user?.subscriber_code || "",
        "Full Name": r.epaper_user?.full_name || "",
        Email: r.epaper_user?.email || "",
        Phone: r.epaper_user?.phone || "",
        "Receipt Number": r.receipt_number || "",
        Amount: r.amount || "",
        "Payment Mode": paymentModeLabels[r.payment_mode] || r.payment_mode || "",
        Plan: r.subscription_plan?.plan_name || "",
        "Subscription Start": formatDate(r.subscription_start_date),
        "Subscription End": formatDate(r.subscription_end_date),
        Status: r.status || "",
        "Payment Date": formatDate(r.payment_date),
        "Agent Code": r.agent_code || "",
        "Recorded On": formatDate(r.createdAt),
      }));

      const ws = XLSX.utils.json_to_sheet(sheetRows);
      const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
      for (let c = range.s.c; c <= range.e.c; c++) {
        const cell = XLSX.utils.encode_cell({ r: 0, c });
        if (ws[cell]) ws[cell].s = { font: { bold: true } };
      }
      ws["!cols"] = [
        { wch: 6 },  // S.No
        { wch: 14 }, // Subscriber Code
        { wch: 22 }, // Full Name
        { wch: 28 }, // Email
        { wch: 15 }, // Phone
        { wch: 30 }, // Receipt Number
        { wch: 10 }, // Amount
        { wch: 18 }, // Payment Mode
        { wch: 20 }, // Plan
        { wch: 18 }, // Subscription Start
        { wch: 18 }, // Subscription End
        { wch: 12 }, // Status
        { wch: 14 }, // Payment Date
        { wch: 12 }, // Agent Code
        { wch: 14 }, // Recorded On
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Subscribers");

      const statusPart = subStatus.length ? subStatus.join("-") : "all";
      const agentPart = agent?.code || agent?.username || id;
      XLSX.writeFile(
        wb,
        `subscribers-${agentPart}-${statusPart}-${moment().format(
          "YYYY-MM-DD"
        )}.xlsx`
      );
      message.success(`Exported ${rows.length} subscriptions to Excel`);

      if (res?.pagination?.total > rows.length) {
        message.warning(
          `Only the first ${rows.length} of ${res.pagination.total} subscriptions were exported`
        );
      }
    } catch (err) {
      console.error("Subscription export failed:", err);
      message.error("Failed to export subscriptions");
    } finally {
      setSubExporting(false);
    }
  };

  const onTabChange = (key: string) => {
    setTab(key as TabKey);
    const next = new URLSearchParams(searchParams);
    next.set("tab", key);
    setSearchParams(next, { replace: true });
  };

  const districtDisplay = useMemo(() => {
    if (!agent) return "—";
    if (agent.district === "Others")
      return agent.custom_district || "Others";
    return agent.district || "—";
  }, [agent]);

  const registeredColumns: ColumnsType<RegisteredUser> = [
    {
      title: "#",
      key: "serial",
      width: 50,
      render: (_t, _r, i) => (regPage - 1) * regPageSize + i + 1,
    },
    {
      title: "Code",
      dataIndex: "subscriber_code",
      key: "subscriber_code",
      width: 90,
      render: (t: string) => (
        <span
          style={{ fontFamily: "monospace", fontWeight: 600, color: "#0576b7" }}
        >
          {t || "-"}
        </span>
      ),
    },
    {
      title: "Full Name",
      dataIndex: "full_name",
      key: "full_name",
      width: 200,
    },
    { title: "Email", dataIndex: "email", key: "email", width: 240 },
    { title: "Phone", dataIndex: "phone", key: "phone", width: 140 },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (s: string) => (
        <Tag color={statusColor(s)} style={{ textTransform: "capitalize" }}>
          {s || "—"}
        </Tag>
      ),
    },
    {
      title: "Register Mode",
      dataIndex: "register_mode",
      key: "register_mode",
      width: 120,
      render: (m: string) => (
        <Tag
          color={m === "agent" ? "blue" : m === "paytabs" ? "green" : "default"}
          style={{ textTransform: "capitalize" }}
        >
          {m === "agent"
            ? "Agent"
            : m === "paytabs"
              ? "Paytabs"
              : m || "Signup"}
        </Tag>
      ),
    },
    {
      title: "Payment",
      dataIndex: "amount",
      key: "amount",
      width: 110,
      render: (a: string | null) => (
        <Tag color={a ? "red" : "geekblue"}>{a ? `Paid ${a}` : "Free"}</Tag>
      ),
    },
    {
      title: "Subscription End",
      dataIndex: "subscription_end_date",
      key: "subscription_end_date",
      width: 150,
      render: (d: string | null) => formatDate(d),
    },
    {
      title: "Registered",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 140,
      render: (d: string) => formatDate(d),
    },
  ];

  const subscriberColumns: ColumnsType<SubscriptionRow> = [
    {
      title: "#",
      key: "serial",
      width: 50,
      render: (_t, _r, i) => (subPage - 1) * subPageSize + i + 1,
    },
    {
      title: "User",
      key: "user",
      width: 200,
      render: (_t, r) => {
        const u = r.epaper_user;
        if (!u) return <span style={{ color: "#999" }}>—</span>;
        return (
          <div>
            <div>{u.full_name || "—"}</div>
            {u.subscriber_code && (
              <div
                style={{
                  fontSize: 11,
                  color: "#0576b7",
                  fontFamily: "monospace",
                }}
              >
                {u.subscriber_code}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Email",
      key: "email",
      width: 220,
      render: (_t, r) =>
        r.epaper_user?.email || <span style={{ color: "#999" }}>—</span>,
    },
    {
      title: "Phone",
      key: "phone",
      width: 140,
      render: (_t, r) =>
        r.epaper_user?.phone || <span style={{ color: "#999" }}>—</span>,
    },
    {
      title: "Receipt #",
      dataIndex: "receipt_number",
      key: "receipt_number",
      width: 200,
      render: (t: string) => (
        <span style={{ fontFamily: "monospace" }}>{t || "N/A"}</span>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 100,
      render: (a: string, record: any) => (
        <span>{formatMoney(a, record?.currency)}</span>
      ),
    },
    {
      title: "Payment Mode",
      dataIndex: "payment_mode",
      key: "payment_mode",
      width: 140,
      render: (m: string) => (
        <Tag color="blue">{paymentModeLabels[m] || m}</Tag>
      ),
    },
    {
      title: "Plan",
      key: "plan",
      width: 160,
      render: (_t, r) => r.subscription_plan?.plan_name || "—",
    },
    {
      title: "Start",
      dataIndex: "subscription_start_date",
      key: "subscription_start_date",
      width: 120,
      render: (d: string) => formatDate(d),
    },
    {
      title: "End",
      dataIndex: "subscription_end_date",
      key: "subscription_end_date",
      width: 120,
      render: (d: string) => formatDate(d),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (s: string) => (
        <Tag color={statusColor(s)} style={{ textTransform: "capitalize" }}>
          {s || "—"}
        </Tag>
      ),
    },
    {
      title: "Payment Date",
      dataIndex: "payment_date",
      key: "payment_date",
      width: 130,
      render: (d: string) => formatDate(d),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Agent"
        loader={agentLoading || regLoading || subLoading}
        length={`Dashboard / Agents / ${agent?.username || "View"}`}
      >
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/Auth/agents")}
          >
            Back
          </Button>
          {agent && (
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => navigate(`/Auth/agents/form/${agent.id}`)}
            >
              Edit Agent
            </Button>
          )}
        </div>
      </PageHeader>

      <div style={{ padding: 10 }}>
        <Card size="small" style={{ marginBottom: 12 }}>
          {agentLoading || !agent ? (
            <Skeleton active paragraph={{ rows: 3 }} />
          ) : (
            <Descriptions
              size="small"
              column={{ xs: 1, sm: 2, md: 3, lg: 4 }}
              title={
                <div
                  style={{ display: "flex", alignItems: "center", gap: 8 }}
                >
                  <span style={{ fontSize: 18, fontWeight: 600 }}>
                    {agent.username}
                  </span>
                  {agent.code && (
                    <Tag color="blue" style={{ fontFamily: "monospace" }}>
                      {agent.code}
                    </Tag>
                  )}
                  <Tag color={agent.blocked ? "red" : "green"}>
                    {agent.blocked ? "Inactive" : "Active"}
                  </Tag>
                </div>
              }
            >
              <Descriptions.Item label="Email">
                {agent.email || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {agent.phone_number || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Country">
                {countryLabel(agent.country) || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Region">
                {/* A region code or "Others"; older agents have a country
                    code or "india"/"gulf". */}
                {regionLabel(agent.region) || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Zone">
                {agent.zone || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="District">
                {districtDisplay}
              </Descriptions.Item>
              <Descriptions.Item label="Role">
                {agent.role?.name || "—"}
              </Descriptions.Item>
            </Descriptions>
          )}
        </Card>

        <Card size="small">
          <Tabs
            activeKey={tab}
            onChange={onTabChange}
            destroyInactiveTabPane={false}
            items={[
              {
                key: "registered",
                label: (
                  <span>
                    Registered Users{" "}
                    <Tag color="blue" style={{ marginLeft: 4 }}>
                      {regTotal}
                    </Tag>
                  </span>
                ),
                children: (
                  <div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 12,
                        flexWrap: "wrap",
                        gap: 8,
                      }}
                    >
                      <span style={{ color: "#888", fontSize: 13 }}>
                        Users this agent personally registered.
                      </span>
                      <Input
                        prefix={<SearchOutlined />}
                        placeholder="Search by name, email, phone, or code"
                        allowClear
                        style={{ width: 320, maxWidth: "100%" }}
                        value={regSearch}
                        onChange={(e) => setRegSearch(e.target.value)}
                      />
                    </div>
                    {regLoading && regData.length === 0 ? (
                      <Skeleton active paragraph={{ rows: 6 }} />
                    ) : (
                      <Table
                        columns={registeredColumns}
                        dataSource={regData}
                        loading={regLoading}
                        pagination={false}
                        size="small"
                        rowKey="id"
                        scroll={{ x: "max-content" }}
                        locale={{
                          emptyText: "No registered users yet",
                        }}
                      />
                    )}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginTop: 12,
                      }}
                    >
                      <Pagination
                        current={regPage}
                        pageSize={regPageSize}
                        total={regTotal}
                        showSizeChanger
                        pageSizeOptions={["10", "20", "50", "100"]}
                        onChange={(p, ps) => {
                          setRegPage(p);
                          setRegPageSize(ps);
                        }}
                        showTotal={(t, r) =>
                          `${r[0]}-${r[1]} of ${t} users`
                        }
                      />
                    </div>
                  </div>
                ),
              },
              {
                key: "subscribers",
                label: (
                  <span>
                    Subscribers{" "}
                    <Tag color="green" style={{ marginLeft: 4 }}>
                      {subTotal}
                    </Tag>
                  </span>
                ),
                children: (
                  <div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 12,
                        flexWrap: "wrap",
                        gap: 8,
                      }}
                    >
                      <span style={{ color: "#888", fontSize: 13 }}>
                        Subscriptions recorded by this agent.
                      </span>
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          flexWrap: "wrap",
                          alignItems: "center",
                        }}
                      >
                        <Select
                          mode="multiple"
                          allowClear
                          placeholder="All statuses"
                          options={subscriptionStatusOptions}
                          value={subStatus}
                          onChange={(v) => setSubStatus(v)}
                          style={{ minWidth: 200 }}
                          maxTagCount="responsive"
                        />
                        <Input
                          prefix={<SearchOutlined />}
                          placeholder="Search by name, email, phone, code or receipt #"
                          allowClear
                          style={{ width: 320, maxWidth: "100%" }}
                          value={subSearch}
                          onChange={(e) => setSubSearch(e.target.value)}
                        />
                        <Button
                          type="primary"
                          icon={<DownloadOutlined />}
                          loading={subExporting}
                          disabled={subTotal === 0}
                          onClick={handleSubExport}
                        >
                          Export Excel
                        </Button>
                      </div>
                    </div>
                    {subLoading && subData.length === 0 ? (
                      <Skeleton active paragraph={{ rows: 6 }} />
                    ) : (
                      <Table
                        columns={subscriberColumns}
                        dataSource={subData}
                        loading={subLoading}
                        pagination={false}
                        size="small"
                        rowKey="id"
                        scroll={{ x: "max-content" }}
                        locale={{
                          emptyText: "No subscriptions recorded yet",
                        }}
                      />
                    )}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginTop: 12,
                      }}
                    >
                      <Pagination
                        current={subPage}
                        pageSize={subPageSize}
                        total={subTotal}
                        showSizeChanger
                        pageSizeOptions={["10", "20", "50", "100"]}
                        onChange={(p, ps) => {
                          setSubPage(p);
                          setSubPageSize(ps);
                        }}
                        showTotal={(t, r) =>
                          `${r[0]}-${r[1]} of ${t} subscriptions`
                        }
                      />
                    </div>
                  </div>
                ),
              },
            ]}
          />
        </Card>
      </div>
    </div>
  );
}

export default AgentView;
